import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Chip,
  Grid,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { DropdownOption } from 'components/atoms/Dropdown/Dropdown';
import AutocompleteFilter from 'components/SearchFilters/MainFilters/AutocompleteFilter';
import { Job, JobArchiveState, JobType } from 'interfaces/job';
import { compact, filter, includes, isEmpty, keyBy, map, pickBy, uniq } from 'lodash';
import React from 'react';
import { useClipboard } from 'use-clipboard-copy';
import { getJobQueryKey } from 'utils/queryHooks/jobs/useJob';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';

import { JobDetailsLink } from './JobDetailsLink';
import { getJobStatusDisplayLabel, JobStatusChip } from './JobStatusChip';
import { getJobTypeDisplayLabel } from './jobTypeLabels';
import { getRelatedJobSortValue, RelatedJobsSortField } from './relatedJobsTableUtils';

interface RelatedJobsTableProps {
  childJobs?: Job[];
  getSubtaskStatusLabel: (status?: string) => string;
}

export const RelatedJobsTable: React.FC<RelatedJobsTableProps> = ({ childJobs = [], getSubtaskStatusLabel }) => {
  const queryClient = useQueryClient();
  const [relatedJobsSortField, setRelatedJobsSortField] = React.useState<RelatedJobsSortField>('status');
  const [relatedJobsSortDirection, setRelatedJobsSortDirection] = React.useState<'asc' | 'desc'>('asc');
  const [relatedJobsTypeFilter, setRelatedJobsTypeFilter] = React.useState<string[]>([]);
  const [relatedJobsStatusFilter, setRelatedJobsStatusFilter] = React.useState<string[]>([]);
  const relatedJobClipboard = useClipboard({ copiedTimeout: 1000 });
  const [copiedRelatedJobIdentifier, setCopiedRelatedJobIdentifier] = React.useState<string>('');

  const childJobIds = map(childJobs, 'id').filter(Boolean) as string[];
  const { data: childJobsData, isFetching: isFetchingChildJobs } = useJobs({
    enabled: !isEmpty(childJobIds),
    noInitialData: true,
    page: 1,
    pageSize: Math.max(childJobIds.length, 1),
    childTasksOnly: true,
    additionalFilters: {
      archiveState: JobArchiveState.All,
      jobIds: childJobIds,
    },
  });

  const relatedChildJobs = React.useMemo(() => {
    const childJobsById = keyBy(childJobsData?.jobs || [], 'id');

    return map(childJobs, (childJob) => {
      if (!childJob?.id) {
        return childJob;
      }

      return childJobsById[childJob.id] || childJob;
    });
  }, [childJobs, childJobsData?.jobs]);

  const relatedJobTypeOptions = React.useMemo(
    () => compact(uniq(map(relatedChildJobs, (childJob) => childJob?.type))),
    [relatedChildJobs]
  );
  const relatedJobTypeFilterOptions = React.useMemo<DropdownOption[]>(
    () =>
      map(relatedJobTypeOptions, (jobType) => ({
        value: jobType,
        text: getJobTypeDisplayLabel(jobType as JobType),
      })),
    [relatedJobTypeOptions]
  );
  const relatedJobStatusOptions = React.useMemo(
    () => compact(uniq(map(relatedChildJobs, (childJob) => childJob?.status))),
    [relatedChildJobs]
  );
  const relatedJobStatusFilterOptions = React.useMemo<DropdownOption[]>(
    () =>
      map(relatedJobStatusOptions, (status) => ({
        value: status,
        text: getJobStatusDisplayLabel(status, {}) || getSubtaskStatusLabel(status),
      })),
    [getSubtaskStatusLabel, relatedJobStatusOptions]
  );
  const shouldShowTypeFilter = relatedJobTypeOptions.length > 1;
  const shouldShowStatusFilter = relatedJobStatusOptions.length > 1;
  const visibleRelatedChildJobs = React.useMemo(() => {
    const filteredRelatedJobs = filter(relatedChildJobs, (relatedJob) => {
      const matchesType = isEmpty(relatedJobsTypeFilter) || includes(relatedJobsTypeFilter, relatedJob?.type || '');
      const matchesStatus =
        isEmpty(relatedJobsStatusFilter) || includes(relatedJobsStatusFilter, relatedJob?.status || '');

      return matchesType && matchesStatus;
    });

    return [...filteredRelatedJobs].sort((leftJob, rightJob) => {
      const leftSortValue = getRelatedJobSortValue(leftJob || {}, relatedJobsSortField, getSubtaskStatusLabel);
      const rightSortValue = getRelatedJobSortValue(rightJob || {}, relatedJobsSortField, getSubtaskStatusLabel);

      if (leftSortValue === rightSortValue) {
        return (leftJob?.id || '').localeCompare(rightJob?.id || '');
      }

      const comparison = leftSortValue.localeCompare(rightSortValue);
      return relatedJobsSortDirection === 'asc' ? comparison : -comparison;
    });
  }, [
    getSubtaskStatusLabel,
    relatedChildJobs,
    relatedJobsSortDirection,
    relatedJobsSortField,
    relatedJobsStatusFilter,
    relatedJobsTypeFilter,
  ]);

  React.useEffect(() => {
    setRelatedJobsSortField('status');
    setRelatedJobsSortDirection('asc');
    setRelatedJobsTypeFilter([]);
    setRelatedJobsStatusFilter([]);
    setCopiedRelatedJobIdentifier('');
  }, [childJobIds.join('|')]);

  React.useEffect(() => {
    map(childJobsData?.jobs || [], (childJob) => {
      if (!childJob?.id) {
        return;
      }

      queryClient.setQueryData(getJobQueryKey(childJob.id), (existingJob?: Job) => {
        if (!existingJob) {
          return childJob;
        }

        return {
          ...existingJob,
          ...pickBy(childJob, (value) => value !== undefined),
        };
      });
    });
  }, [childJobsData?.jobs, queryClient]);

  const handleSortRelatedJobs = (sortField: RelatedJobsSortField) => {
    if (relatedJobsSortField === sortField) {
      setRelatedJobsSortDirection((currentDirection) => (currentDirection === 'asc' ? 'desc' : 'asc'));
      return;
    }

    setRelatedJobsSortField(sortField);
    setRelatedJobsSortDirection('asc');
  };

  const handleCopyRelatedJobIdentifier = (relatedJobIdentifier: string) => {
    relatedJobClipboard.copy(relatedJobIdentifier);
    setCopiedRelatedJobIdentifier(relatedJobIdentifier);
  };

  const toggleRelatedJobsStatusFilter = (statusValue: string) => {
    setRelatedJobsStatusFilter((currentStatuses) =>
      includes(currentStatuses, statusValue)
        ? filter(currentStatuses, (value) => value !== statusValue)
        : [...currentStatuses, statusValue]
    );
  };

  const renderRelatedJobsTypeTags = React.useCallback(
    (selectedOptions: Array<DropdownOption | string | number>, getTagProps: any) => {
      if (isEmpty(selectedOptions)) {
        return null;
      }

      const firstOption = selectedOptions[0];
      const firstLabel =
        typeof firstOption === 'string' || typeof firstOption === 'number'
          ? String(firstOption)
          : firstOption?.text || '';

      if (selectedOptions.length === 1) {
        return (
          <Chip
            {...getTagProps({ index: 0 })}
            label={firstLabel}
            size="small"
            sx={{
              maxWidth: 'calc(100% - 44px)',
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              },
            }}
          />
        );
      }

      return (
        <Chip
          label={`${selectedOptions.length} job types selected`}
          size="small"
          onDelete={() => setRelatedJobsTypeFilter([])}
          sx={{
            maxWidth: 'calc(100% - 44px)',
            '& .MuiChip-label': {
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
          }}
        />
      );
    },
    []
  );

  if (isEmpty(relatedChildJobs)) {
    return null;
  }

  return (
    <Grid item container direction="column" spacing={1}>
      <Grid item container justifyContent="space-between" spacing={1} alignItems="flex-end">
        <Grid item>
          <Typography variant="h4">Related Jobs</Typography>
        </Grid>
        {(shouldShowTypeFilter || shouldShowStatusFilter) && (
          <Grid item container spacing={1} width="auto" sx={{ maxWidth: '100%' }}>
            {shouldShowTypeFilter && (
              <Grid item xs={12} sm="auto" sx={{ minWidth: 0, maxWidth: '100%' }}>
                <Box
                  sx={{
                    minWidth: { xs: 0, sm: 200 },
                    width: { xs: '100%', sm: 320 },
                    maxWidth: '100%',
                    '& .MuiAutocomplete-root': {
                      width: '100%',
                      maxWidth: '100%',
                    },
                    '& .MuiAutocomplete-inputRoot': {
                      minWidth: 0,
                      pr: '72px !important',
                    },
                    '& .MuiAutocomplete-tag': {
                      maxWidth: '100%',
                    },
                    '& .MuiAutocomplete-endAdornment': {
                      top: '50%',
                      transform: 'translateY(-50%)',
                    },
                  }}
                >
                  <AutocompleteFilter
                    label="Filter by job type"
                    options={relatedJobTypeFilterOptions}
                    limitTags={1}
                    handleChange={setRelatedJobsTypeFilter}
                    renderTags={renderRelatedJobsTypeTags}
                    value={relatedJobsTypeFilter}
                  />
                </Box>
              </Grid>
            )}
            {shouldShowStatusFilter && (
              <Grid item xs={12}>
                <Grid container spacing={1} alignItems="center">
                  <Grid item>
                    <Typography variant="body2" color="text.secondary">
                      Quick status filters:
                    </Typography>
                  </Grid>
                  <Grid item xs>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {map(relatedJobStatusFilterOptions, (statusOption) => {
                        const isSelected = includes(relatedJobsStatusFilter, statusOption.value as string);

                        return (
                          <Chip
                            key={statusOption.value}
                            label={statusOption.text}
                            size="small"
                            color={isSelected ? 'primary' : 'default'}
                            variant={isSelected ? 'filled' : 'outlined'}
                            onClick={() => toggleRelatedJobsStatusFilter(statusOption.value as string)}
                          />
                        );
                      })}
                    </Stack>
                  </Grid>
                </Grid>
              </Grid>
            )}
          </Grid>
        )}
      </Grid>
      <Grid item>
        <TableContainer>
          <Table size="small" aria-label="Related jobs table" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '52%', pl: 0 }}>
                  <TableSortLabel
                    active={relatedJobsSortField === 'type'}
                    direction={relatedJobsSortField === 'type' ? relatedJobsSortDirection : 'asc'}
                    onClick={() => handleSortRelatedJobs('type')}
                  >
                    <Typography variant="subtitle2">Job Type</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ width: '18%' }}>
                  <TableSortLabel
                    active={relatedJobsSortField === 'job'}
                    direction={relatedJobsSortField === 'job' ? relatedJobsSortDirection : 'asc'}
                    onClick={() => handleSortRelatedJobs('job')}
                  >
                    <Typography variant="subtitle2">Job</Typography>
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right" sx={{ width: '30%', pr: 0, whiteSpace: 'nowrap' }}>
                  <TableSortLabel
                    active={relatedJobsSortField === 'status'}
                    direction={relatedJobsSortField === 'status' ? relatedJobsSortDirection : 'asc'}
                    onClick={() => handleSortRelatedJobs('status')}
                  >
                    <Typography variant="subtitle2">Status</Typography>
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {map(visibleRelatedChildJobs, (childJob) => {
                const relatedJobId = childJob?.id || '';
                const shouldShowTypeSkeleton = !childJob?.type && isFetchingChildJobs;

                return (
                  <TableRow key={`related-child-job-${childJob?.id || 'unknown'}`}>
                    <TableCell sx={{ pl: 0, verticalAlign: 'top' }}>
                      {shouldShowTypeSkeleton ? (
                        <Skeleton variant="text" width="70%" />
                      ) : (
                        <Typography variant="body1">{getJobTypeDisplayLabel(childJob?.type as JobType)}</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ minWidth: 0, verticalAlign: 'top' }}>
                      {childJob?.id ? (
                        <Tooltip
                          title={
                            <Box sx={{ maxWidth: 320 }}>
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {relatedJobId}
                              </Typography>
                              <Button
                                size="small"
                                startIcon={<ContentCopyIcon fontSize="small" />}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  handleCopyRelatedJobIdentifier(relatedJobId);
                                }}
                                sx={{
                                  mt: 1,
                                  minWidth: 0,
                                  color: 'common.white',
                                  '& .MuiButton-startIcon': {
                                    color: 'common.white',
                                  },
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.12)',
                                  },
                                }}
                              >
                                {relatedJobClipboard.copied && copiedRelatedJobIdentifier === relatedJobId
                                  ? 'Copied'
                                  : 'Copy'}
                              </Button>
                            </Box>
                          }
                        >
                          <Box>
                            <JobDetailsLink
                              jobId={childJob.id}
                              variant="body2"
                              ariaLabel={`View job ${childJob.id}`}
                              title={relatedJobId}
                              sx={{
                                whiteSpace: 'nowrap',
                                display: 'inline-flex',
                                alignItems: 'center',
                                py: 0.25,
                                borderRadius: 1,
                                color: 'primary.main',
                                fontWeight: 600,
                                textDecoration: 'underline',
                                textUnderlineOffset: '0.15em',
                                '&:hover': {
                                  backgroundColor: 'action.hover',
                                },
                              }}
                            >
                              View
                            </JobDetailsLink>
                          </Box>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell align="right" sx={{ pr: 0, verticalAlign: 'top' }}>
                      <Stack direction="row" spacing={0.75} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                        {childJob?.status ? (
                          <JobStatusChip status={childJob.status} label={getSubtaskStatusLabel(childJob.status)} />
                        ) : null}
                        {childJob?.deletedAt ? (
                          <Chip size="small" variant="outlined" label="Archived" aria-label="Archived job" />
                        ) : null}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
              {isEmpty(visibleRelatedChildJobs) && (
                <TableRow>
                  <TableCell colSpan={3} sx={{ pl: 0, pr: 0 }}>
                    <Typography variant="body2" color="text.secondary">
                      No related jobs match the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
};
